/* ==========================================================================
   facets.js — コレクション・検索の絞り込み / 並び替え / ページ送り
   <facet-form data-section-id="..."> で囲まれた範囲を扱う。
   - [data-facets-form]  : 絞り込みフォーム（サイドバー・ドロワーの2つがあり得る）
   - [data-facets-sort]  : 並び替え select
   - [data-facets-link]  : Ajax で開くリンク（チップ削除・ページ番号など）
   - [data-load-more]    : 「もっと見る」
   - [data-facets-replace="key"] : 再描画時に同じ key の要素と差し替える
   ========================================================================== */
import { define, debounce, parseHTML, initReveal } from '@theme/global';

class FacetForm extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.debouncedChange = debounce((target) => this.onChange(target), 500);

    this.addEventListener('change', (e) => {
      if (e.target.closest('[data-facets-form]') || e.target.matches('[data-facets-sort]')) {
        this.debouncedChange(e.target);
      }
    });
    this.addEventListener('input', (e) => {
      if (e.target.type === 'number' && e.target.closest('[data-facets-form]')) this.debouncedChange(e.target);
    });
    this.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-facets-form], [data-facets-sort-form]');
      if (!form) return;
      e.preventDefault();
      this.onChange(form.querySelector('[data-facets-sort]') || form);
    });
    this.addEventListener('click', (e) => {
      const more = e.target.closest('a[data-load-more]');
      if (more) { e.preventDefault(); this.loadMore(more); return; }
      const link = e.target.closest('a[data-facets-link]');
      if (link) {
        e.preventDefault();
        const scroll = !!link.closest('.pagination');
        this.navigate(new URL(link.href, location.origin).search, { push: true, scroll });
      }
    });

    this.onPopState = () => this.navigate(location.search, { push: false });
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  get sortValue() {
    return this.querySelector('[data-facets-sort]')?.value;
  }

  paramsFromForm(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== '') params.append(key, value);
    }
    return params;
  }

  onChange(target) {
    let params;
    const form = target.closest?.('[data-facets-form]') || (target.matches?.('[data-facets-form]') ? target : null);
    if (form) {
      params = this.paramsFromForm(form);
    } else {
      // 並び替えのみ変更: 現在の URL の条件を引き継ぐ
      params = new URLSearchParams(location.search);
    }
    if (this.sortValue) params.set('sort_by', this.sortValue);
    params.delete('page');
    this.navigate(`?${params.toString()}`, { push: true });
  }

  buildUrl(search) {
    const query = search.startsWith('?') ? search.slice(1) : search;
    return `${location.pathname}${query ? `?${query}` : ''}`;
  }

  async navigate(search, { push = true, scroll = false } = {}) {
    this.controller?.abort();
    this.controller = new AbortController();
    const url = this.buildUrl(search);
    const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${this.sectionId}`;
    const results = this.querySelector('[data-facets-replace="results"]');
    results?.setAttribute('aria-busy', 'true');

    let html;
    try {
      const res = await fetch(fetchUrl, { signal: this.controller.signal });
      html = await res.text();
    } catch (err) {
      if (err.name !== 'AbortError') {
        results?.removeAttribute('aria-busy');
        location.href = url; // 失敗時は通常の遷移にフォールバック
      }
      return;
    }

    const doc = parseHTML(html);
    const openIds = new Set([...this.querySelectorAll('details[open][id]')].map((d) => d.id));
    const knownIds = new Set([...this.querySelectorAll('details[id]')].map((d) => d.id));
    const focusedId = document.activeElement?.id;

    this.querySelectorAll('[data-facets-replace]').forEach((el) => {
      const next = doc.querySelector(`[data-facets-replace="${el.dataset.facetsReplace}"]`);
      if (next) el.replaceWith(next);
      else if (el.dataset.facetsReplace === 'active') el.replaceChildren();
    });

    // 開閉状態を引き継ぐ（新しく出たフィルターはサーバーの初期状態のまま）
    this.querySelectorAll('details[id]').forEach((d) => {
      if (knownIds.has(d.id)) d.open = openIds.has(d.id);
    });

    const sortSelect = this.querySelector('[data-facets-sort]');
    const nextSort = new URLSearchParams(search).get('sort_by');
    if (sortSelect && nextSort) sortSelect.value = nextSort;

    if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
    if (push) history.pushState({ facets: true }, '', url);
    if (scroll) this.scrollIntoView({ block: 'start' });
    initReveal(this);
  }

  async loadMore(link) {
    link.setAttribute('aria-busy', 'true');
    const url = new URL(link.href, location.origin);
    url.searchParams.set('section_id', this.sectionId);
    try {
      const doc = parseHTML(await (await fetch(url)).text());
      const grid = this.querySelector('[data-facets-grid]');
      const items = doc.querySelectorAll('[data-facets-grid] > li:not(.collection__promo)');
      grid?.append(...items);
      const wrapper = this.querySelector('[data-load-more-wrapper]');
      const nextWrapper = doc.querySelector('[data-load-more-wrapper]');
      if (wrapper && nextWrapper) wrapper.replaceWith(nextWrapper);
      else wrapper?.remove();
      initReveal(this);
    } catch (err) {
      location.href = link.href;
    }
  }
}

define('facet-form', FacetForm);
